import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { type CustomUser, TenantEmailDiscoveryResponse } from 'api';
import { authAction, firmsAction } from 'common/actions';
import {
  AUTH_LOADING_LABELS,
  AUTH_STORAGE_PERSISTENCE_LOCAL,
  AUTH_STORAGE_PERSISTENCE_SESSION,
  SIGN_IN_PRODUCT,
  SIGN_IN_VIEW,
  type SignInProduct,
} from 'common/constants/auth';
import { useStore } from 'common/store';
import { FIRM_LIST_STATUSES } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { WORKBOARD_CURRENT_VALUATIONS_URL } from 'dashboard409a/common/config/urls';
import { getPreviousWorkboardRoute } from 'dashboard409a/utilities/navigation';
import { buildResendActivationUrl, RESEND_ACTIVATION_LINK_LABEL } from 'pages/Auth/ActivateAccount/ResendActivation';
import {
  AuthFormHeader,
  AuthPageStack,
  AuthStatusActionLink,
} from 'pages/Auth/components';
import type { CompanySelectorPermission } from 'pages/Auth/components/CompanySelector/CompanySelector.props';
import type { LoginFirm } from 'pages/Auth/components/FirmList';
import { resolveWorkspaceAutoRedirectPlan } from 'pages/Auth/components/WorkspaceSelector/hooks/useWorkspaceAutoRedirects/resolveWorkspaceAutoRedirect';
import {
  useAuthRequirements,
  useAuthRouteState,
  useAuthSocialSignIn,
  useAuthViewTransition,
  useClearFieldErrorOnValueChange,
  useGetAccessTokens,
  useGetSocialAccessToken,
  useGetUserInfo,
  useLogOutUser,
  useUpdateUserProfileInfo,
  useVerifyAccessToken,
} from 'pages/Auth/hooks';
import { buildResetPasswordUrl } from 'pages/Auth/ResetPassword';
import {
  clearEnterpriseSsoSignInSession,
  clearFieldErrorsByType,
  clearPendingBackupCodes,
  clearPendingSignInIdentifier,
  getPendingSignInIdentifier,
  getRememberSignInPreference,
  getStoredAccessToken,
  setOtpVerified,
  setPendingSignInIdentifier,
  setRememberSignInPreference,
} from 'pages/Auth/utils';
import {
  SignInAlerts,
  SignInFooter,
  SignInHeaderTitle,
  SignInStepContent,
} from './components';
import {
  useResolvePostAuthView,
  useRouteAuthErrorToActiveStep,
  useSignInRouteGuards,
  useSignInSsoDiscovery,
  useSignInViewModel,
} from './hooks';
import {
  AUTH_LOGIN_ERROR,
  SIGN_IN_ACTIVATION_RECOVERY_PROMPT,
  SIGN_IN_PASSWORD_INVALID_ERROR,
  SIGN_IN_TERMS_SAVE_ERROR,
} from './SignIn.constants';
import { IDENTIFIER_SCHEMA, PASSWORD_SCHEMA, TERMS_SCHEMA } from './SignIn.schema';
import { IdentifierFormValues, PasswordFormValues, TermsFormValues } from './SignIn.types';
import {
  isEmailLike,
  isInvalidCredentialsError,
  isServiceError,
  resolveCompletedTwoFactorSignInView,
  shouldShowSignInPostAuthResolutionLoading,
  signInViewRequiresSession,
} from './SignIn.utils';

const SignIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const logOutUser = useLogOutUser();
  const verifyAccessToken = useVerifyAccessToken({ useGlobalLoading: false });
  const getAccessTokens = useGetAccessTokens({ useGlobalLoading: false });
  const getUserInfo = useGetUserInfo({ useGlobalLoading: false });
  const getSocialAccessToken = useGetSocialAccessToken({ useGlobalLoading: false });
  const updateUserProfileInfo = useUpdateUserProfileInfo({ useGlobalLoading: false });
  const [{ user }, dispatch] = useStore() as UseStoreValues;
  const currentUser = useMemo(() => (!isEmpty(user) ? (user as CustomUser) : undefined), [user]);
  const accessToken = getStoredAccessToken();
  const [authError, setAuthError] = useState<string | null>(null);
  const [isRequirementsErrorDismissed, setIsRequirementsErrorDismissed] = useState(false);
  const [isIdentifierSubmitting, setIsIdentifierSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [isPasswordDiscoveryRestoring, setIsPasswordDiscoveryRestoring] = useState(false);
  const [isTermsSubmitting, setIsTermsSubmitting] = useState(false);
  const [pendingProductSelection, setPendingProductSelection] = useState<SignInProduct | null>(null);
  const [rememberSignIn, setRememberSignIn] = useState(() => getRememberSignInPreference());
  const { mode, product, returnTo, source, updateRouteState, view } = useAuthRouteState(SIGN_IN_VIEW.IDENTIFIER);
  const { isTransitioningToView, startViewTransition } = useAuthViewTransition(view);
  const initialViewRef = useRef(view);
  const previousViewRef = useRef(view);
  const [restoredPasswordStepIdentifier] = useState(() => (
    view === SIGN_IN_VIEW.PASSWORD ? getPendingSignInIdentifier() : null
  ));
  const authRequirements = useAuthRequirements(currentUser);
  const userHasTwoFactor = useMemo(
    () => authRequirements.userHasTwoFactor || Boolean(currentUser?.has_2fa),
    [authRequirements.userHasTwoFactor, currentUser?.has_2fa]
  );

  const identifierForm = useForm<IdentifierFormValues>({
    defaultValues: {
      identifier: restoredPasswordStepIdentifier ?? '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(IDENTIFIER_SCHEMA),
  });
  const passwordForm = useForm<PasswordFormValues>({
    defaultValues: {
      password: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(PASSWORD_SCHEMA),
  });

  const setRememberSignInPersisted = useCallback((value: boolean) => {
    setRememberSignIn(value);
    setRememberSignInPreference(value);
  }, []);
  const termsForm = useForm<TermsFormValues>({
    defaultValues: {
      acceptTerms: false,
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(TERMS_SCHEMA),
  });
  const watchedIdentifier = identifierForm.watch('identifier');
  const watchedPassword = passwordForm.watch('password');
  const watchedAcceptedTerms = termsForm.watch('acceptTerms');
  // The sign-in identifier stays out of URLs; password refresh can restore it from tab-local storage only.
  const identifier = useMemo(() => {
    const trimmedIdentifier = watchedIdentifier.trim();
    return trimmedIdentifier || null;
  }, [watchedIdentifier]);

  const clearIdentifierServerError = useCallback(() => {
    clearFieldErrorsByType(identifierForm, ['identifier'], ['server']);
  }, [identifierForm]);

  const clearPasswordServerError = useCallback(() => {
    clearFieldErrorsByType(passwordForm, ['password'], ['server']);
  }, [passwordForm]);

  const clearTermsServerError = useCallback(() => {
    clearFieldErrorsByType(termsForm, ['acceptTerms'], ['server']);
  }, [termsForm]);

  const dismissAuthError = useCallback(() => {
    setAuthError(null);
    clearIdentifierServerError();
    clearPasswordServerError();
    clearTermsServerError();
  }, [clearIdentifierServerError, clearPasswordServerError, clearTermsServerError]);
  // Terminal auth steps clear the tab-local identifier so stale PII is not kept around after the flow advances.
  const clearIdentifierState = useCallback(() => {
    identifierForm.reset({ identifier: '' });
    clearPendingSignInIdentifier();
  }, [identifierForm]);
  const {
    handleGoogleLogin,
    handleMicrosoftLogin,
    isSocialPending,
    socialProviderPending,
  } = useAuthSocialSignIn({
    getSocialAccessToken,
    onSuccess: () => {
      clearIdentifierState();
      // Social auth enters a session-bound loading surface; useResolvePostAuthView owns the real next route.
      startViewTransition(SIGN_IN_VIEW.WORKSPACE_SELECTION);
      updateRouteState({ mode: null, view: SIGN_IN_VIEW.WORKSPACE_SELECTION });
    },
    persistence: rememberSignIn ? AUTH_STORAGE_PERSISTENCE_LOCAL : AUTH_STORAGE_PERSISTENCE_SESSION,
    setAuthError,
  });

  const { discoverSso, resetSsoDiscovery, ssoDiscovery } = useSignInSsoDiscovery();
  const {
    loadingLabel,
    normalizedRouteState,
    productAccess,
    productSelectionOptions,
    resolvedProduct,
    safeReturnTo,
    showFooterSkip,
    showSignUpFooter,
    signUpHref,
    shouldShowFlowLoading,
  } = useSignInViewModel({
    accessToken,
    authRequirements,
    currentUser,
    mode,
    product,
    returnTo,
    source,
    view,
  });
  const resolvedFirmList = useMemo(
    () => (authRequirements.firmList as LoginFirm[] | null | undefined) ?? null,
    [authRequirements.firmList]
  );
  const resolvedStandaloneCompaniesPermissions = useMemo(
    () =>
      (currentUser?.standalone_companies_permissions ?? []).filter(
        (company): company is CompanySelectorPermission => Boolean(company.id != null && company.slug)
      ),
    [currentUser?.standalone_companies_permissions]
  );
  const hasNotFirmPermissions = useMemo(
    () => Boolean(
      resolvedFirmList?.every(firm => firm.user_permissions?.length === 0)
      && currentUser
      && !currentUser.is_superuser
    ),
    [currentUser, resolvedFirmList]
  );
  // Deterministic workspace redirects should stay on a loading surface so the auth shell does not flash a selector card it will never keep mounted.
  const workspaceAutoRedirectPlan = useMemo(
    () => resolveWorkspaceAutoRedirectPlan({
      hasNotFirmPermissions,
      isCompanyProduct: resolvedProduct === SIGN_IN_PRODUCT.COMPANY,
      locationState: location.state,
      lowerPermissions: authRequirements.lowerPermissions,
      resolvedFirmList,
      resolvedStandaloneCompaniesPermissions,
      resolvedUser: currentUser
        ? {
          id: currentUser.id,
          is_staff: currentUser.is_staff,
          is_superuser: currentUser.is_superuser,
        }
        : undefined,
    }),
    [
      authRequirements.lowerPermissions,
      currentUser,
      hasNotFirmPermissions,
      location.state,
      resolvedFirmList,
      resolvedProduct,
      resolvedStandaloneCompaniesPermissions,
    ]
  );
  const shouldShowWorkspaceRedirectLoading = view === SIGN_IN_VIEW.WORKSPACE_SELECTION
    && Boolean(workspaceAutoRedirectPlan && workspaceAutoRedirectPlan.kind !== 'clear-app-auto-select');

  const resolvedPostAuthView = useResolvePostAuthView({
    authRequirements,
    currentUser,
    navigate,
    resolvedProduct,
    safeReturnTo,
    updateRouteState,
    view,
  });
  const shouldShowPostAuthResolutionLoading = shouldShowSignInPostAuthResolutionLoading({
    resolvedView: resolvedPostAuthView,
    view,
  });
  const shouldShowPasswordDiscoveryLoading = isPasswordDiscoveryRestoring;
  const shouldShowTwoFactorCompletionLoading = view === SIGN_IN_VIEW.TWO_FACTOR
    && (
      isTransitioningToView(SIGN_IN_VIEW.TERMS)
      || isTransitioningToView(SIGN_IN_VIEW.PRODUCT_SELECTION)
      || isTransitioningToView(SIGN_IN_VIEW.WORKSPACE_SELECTION)
    );
  const shouldShowCurrentFlowLoading = shouldShowFlowLoading
    || shouldShowPasswordDiscoveryLoading
    || shouldShowPostAuthResolutionLoading
    || shouldShowTwoFactorCompletionLoading;
  const currentLoadingLabel = shouldShowPasswordDiscoveryLoading || shouldShowTwoFactorCompletionLoading
    ? AUTH_LOADING_LABELS.nextStep
    : loadingLabel;
  const shouldHidePostAuthLoadingHeader = signInViewRequiresSession(view)
    && (shouldShowFlowLoading || shouldShowPostAuthResolutionLoading || shouldShowWorkspaceRedirectLoading);
  const resetPasswordHref = useMemo(
    () => buildResetPasswordUrl({
      email: identifier,
      returnTo: safeReturnTo,
      source,
    }),
    [identifier, safeReturnTo, source]
  );
  const resendActivationHref = useMemo(
    () => buildResendActivationUrl({
      email: identifier,
      returnTo: safeReturnTo,
      source,
    }),
    [identifier, safeReturnTo, source]
  );
  const shouldOfferActivationRecovery = useMemo(
    () =>
      view === SIGN_IN_VIEW.PASSWORD
      && authError === SIGN_IN_PASSWORD_INVALID_ERROR
      && identifier !== null
      && isEmailLike(identifier),
    [authError, identifier, view]
  );
  const authErrorAlertContent = useMemo(() => {
    if (!authError) {
      return null;
    }

    if (!shouldOfferActivationRecovery) {
      return authError;
    }

    // The backend reuses this response for more than one failed-login case, so the alert offers
    // activation recovery without asserting that inactivity is the only explanation.
    return (
      <>
        {authError}. {SIGN_IN_ACTIVATION_RECOVERY_PROMPT}{' '}
        <AuthStatusActionLink<typeof RouterLink> component={RouterLink} to={resendActivationHref}>
          {RESEND_ACTIVATION_LINK_LABEL}
        </AuthStatusActionLink>
        .
      </>
    );
  }, [authError, resendActivationHref, shouldOfferActivationRecovery]);

  // Route the shared auth error to the active step so each form owns its own inline feedback.
  useRouteAuthErrorToActiveStep({
    authError,
    clearIdentifierServerError,
    clearPasswordServerError,
    clearTermsServerError,
    identifierForm,
    passwordForm,
    termsForm,
    view,
  });
  useClearFieldErrorOnValueChange({
    errorTypes: ['server'],
    fieldNames: ['identifier'],
    form: identifierForm,
    onClear: dismissAuthError,
    shouldClear: () => Boolean(authError) && view === SIGN_IN_VIEW.IDENTIFIER,
    values: [watchedIdentifier],
  });
  useClearFieldErrorOnValueChange({
    errorTypes: ['server'],
    fieldNames: ['password'],
    form: passwordForm,
    onClear: dismissAuthError,
    shouldClear: () => Boolean(authError) && view === SIGN_IN_VIEW.PASSWORD,
    values: [watchedPassword],
  });
  useClearFieldErrorOnValueChange({
    errorTypes: ['server'],
    fieldNames: ['acceptTerms'],
    form: termsForm,
    onClear: dismissAuthError,
    shouldClear: () => Boolean(authError) && view === SIGN_IN_VIEW.TERMS,
    values: [watchedAcceptedTerms],
  });

  useSignInRouteGuards({
    accessToken,
    currentUser,
    identifier,
    mode,
    normalizedRouteState,
    product,
    returnTo,
    source,
    updateRouteState,
    verifyAccessToken,
    view,
  });
  // Direct visits to non-password sign-in views should not keep a stale password-step identifier.
  useEffect(() => {
    if (initialViewRef.current !== SIGN_IN_VIEW.PASSWORD) {
      clearPendingSignInIdentifier();
    }
  }, []);

  useEffect(() => {
    if (view === SIGN_IN_VIEW.IDENTIFIER || view === SIGN_IN_VIEW.PASSWORD) {
      return;
    }

    if (identifier) {
      clearIdentifierState();
    }
  }, [clearIdentifierState, identifier, view]);

  // Identifier-step renders can happen while transitioning into password, so only clear after password is left.
  useEffect(() => {
    const previousView = previousViewRef.current;
    previousViewRef.current = view;

    if (previousView === SIGN_IN_VIEW.PASSWORD && view !== SIGN_IN_VIEW.PASSWORD) {
      clearPendingSignInIdentifier();
    }
  }, [view]);

  // Refresh-restored password screens need SSO/branding discovery before rendering the password form.
  useEffect(() => {
    if (
      view !== SIGN_IN_VIEW.PASSWORD
      || !restoredPasswordStepIdentifier
      || identifier !== restoredPasswordStepIdentifier
    ) {
      setIsPasswordDiscoveryRestoring(false);
      return undefined;
    }

    if (!identifier || ssoDiscovery || !isEmailLike(identifier)) {
      return undefined;
    }

    let isCurrent = true;
    setIsPasswordDiscoveryRestoring(true);
    discoverSso(identifier).finally(() => {
      if (isCurrent) {
        setIsPasswordDiscoveryRestoring(false);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [discoverSso, identifier, restoredPasswordStepIdentifier, ssoDiscovery, view]);

  useEffect(() => {
    if (view !== SIGN_IN_VIEW.PASSWORD && isPasswordSubmitting) {
      setIsPasswordSubmitting(false);
    }
  }, [isPasswordSubmitting, view]);

  // Backup codes stay mounted until 2FA exits so the setup/verify screens do not flash during route resolution.
  useEffect(() => {
    if (view !== SIGN_IN_VIEW.TWO_FACTOR) {
      clearPendingBackupCodes();
    }
  }, [view]);

  useEffect(() => {
    if (view !== SIGN_IN_VIEW.PRODUCT_SELECTION && pendingProductSelection !== null) {
      setPendingProductSelection(null);
    }
  }, [pendingProductSelection, view]);

  const hydrateWorkspaceSelectorState = useCallback(() => {
    if (authRequirements.firmListStatus === FIRM_LIST_STATUSES.IDLE) {
      return;
    }

    // Reuse the auth bootstrap instead of forcing workspace selection to refetch the same firm list
    // from scratch after the post-auth resolver has already decided the next destination.
    dispatch(firmsAction.setFirmList(authRequirements.firmList));
    dispatch(firmsAction.setFirmListStatus(authRequirements.firmListStatus));
  }, [authRequirements.firmList, authRequirements.firmListStatus, dispatch]);

  useEffect(() => {
    if (view !== SIGN_IN_VIEW.WORKSPACE_SELECTION) {
      return;
    }

    hydrateWorkspaceSelectorState();
  }, [hydrateWorkspaceSelectorState, view]);

  const handleIdentifierSubmit = identifierForm.handleSubmit(async values => {
    const trimmedIdentifier = values.identifier.trim();
    setAuthError(null);
    resetSsoDiscovery();

    if (trimmedIdentifier !== values.identifier) {
      identifierForm.setValue('identifier', trimmedIdentifier, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    }

    setIsIdentifierSubmitting(true);

    try {
      const discovery = await discoverSso(trimmedIdentifier);
      // SAML-only tenants should leave the app immediately instead of rendering a password step they cannot use.
      if (
        discovery?.post_discovery_flow === TenantEmailDiscoveryResponse.post_discovery_flow.SAML
        && discovery.login_url
      ) {
        window.location.assign(discovery.login_url);
        return;
      }

      // Keep only a short-lived tab-local copy so refresh can restore the password step without leaking PII into the URL.
      setPendingSignInIdentifier(trimmedIdentifier);
      startViewTransition(SIGN_IN_VIEW.PASSWORD);
      updateRouteState({
        product: null,
        view: SIGN_IN_VIEW.PASSWORD,
      });
    } finally {
      setIsIdentifierSubmitting(false);
    }
  });

  const handlePasswordSubmit = passwordForm.handleSubmit(async values => {
    if (isPasswordSubmitting) {
      return;
    }

    if (!identifier) {
      updateRouteState({ view: SIGN_IN_VIEW.IDENTIFIER });
      return;
    }

    clearPasswordServerError();
    setAuthError(null);
    setIsPasswordSubmitting(true);

    try {
      const response = await getAccessTokens(
        identifier,
        values.password,
        () => undefined,
        rememberSignIn ? AUTH_STORAGE_PERSISTENCE_LOCAL : AUTH_STORAGE_PERSISTENCE_SESSION
      );
      if (!response) {
        setAuthError(AUTH_LOGIN_ERROR);
        setIsPasswordSubmitting(false);
        return;
      }

      if (isServiceError(response)) {
        if (isInvalidCredentialsError(response)) {
          passwordForm.setError('password', {
            message: SIGN_IN_PASSWORD_INVALID_ERROR,
            type: 'server',
          });
          setAuthError(SIGN_IN_PASSWORD_INVALID_ERROR);
          setIsPasswordSubmitting(false);
          return;
        }

        setAuthError(AUTH_LOGIN_ERROR);
        setIsPasswordSubmitting(false);
        return;
      }

      // Keep the password step mounted until the post-auth resolver picks the real next screen
      // so the user's primary action stays in a single loading state instead of flashing a new card.
      clearEnterpriseSsoSignInSession();
    } catch {
      setAuthError(AUTH_LOGIN_ERROR);
      setIsPasswordSubmitting(false);
    }
  });

  const handleTermsSubmit = termsForm.handleSubmit(async () => {
    if (isTermsSubmitting) {
      return;
    }

    if (!currentUser?.id) {
      return;
    }

    clearTermsServerError();
    setAuthError(null);
    setIsTermsSubmitting(true);
    const agreedAt = new Date().toISOString();
    try {
      const [updatedUser, error] = await updateUserProfileInfo({
        email: currentUser.email,
        first_name: currentUser.first_name,
        last_name: currentUser.last_name,
        profile: {
          id: currentUser.profile?.id,
          picture_url: currentUser.profile?.picture_url,
          terms_last_agreed: agreedAt,
        },
        username: currentUser.username,
      });

      if (error || !updatedUser) {
        setAuthError(SIGN_IN_TERMS_SAVE_ERROR);
        return;
      }

      dispatch(authAction.setUserInfo(updatedUser));
      await getUserInfo();
    } finally {
      setIsTermsSubmitting(false);
    }
  });

  const handleTwoFactorComplete = () => {
    if (!currentUser?.id) {
      return;
    }

    const nextView = resolveCompletedTwoFactorSignInView({
      firmListCount: authRequirements.firmList.length,
      requiresTerms: !currentUser.profile?.terms_last_agreed,
      requiresTwoFactor: authRequirements.requiresTwoFactor,
      resolvedProduct,
      user: currentUser,
    });

    // Keep the post-auth resolver aligned with the verified OTP state until the backend user payload refreshes.
    dispatch(authAction.setUserInfo({
      ...currentUser,
      has_2fa: 'true',
    }));
    setOtpVerified();

    // Deep links must skip workspace hydration/transitions; otherwise firm auto-redirect to /home
    // can race the returnTo navigation and abandon the original protected URL.
    if (safeReturnTo && nextView !== SIGN_IN_VIEW.TWO_FACTOR && nextView !== SIGN_IN_VIEW.TERMS) {
      navigate(safeReturnTo, { replace: true });
      return;
    }

    if (nextView === SIGN_IN_VIEW.WORKSPACE_SELECTION) {
      // Reuse the firm bootstrap before leaving 2FA so the next auth step does not flash
      // a second "Loading your firms" state when the selector data is already known.
      hydrateWorkspaceSelectorState();
    }

    if (nextView !== SIGN_IN_VIEW.TWO_FACTOR) {
      startViewTransition(nextView);
    }

    updateRouteState({
      mode: null,
      product: nextView === SIGN_IN_VIEW.PRODUCT_SELECTION ? null : resolvedProduct,
      view: nextView,
    });
  };

  const isIdentifierStepSubmitting = isIdentifierSubmitting || isTransitioningToView(SIGN_IN_VIEW.PASSWORD);
  const isSocialStepPending = isSocialPending || isTransitioningToView(SIGN_IN_VIEW.WORKSPACE_SELECTION);
  const shouldShowSignInFooter = showSignUpFooter && !shouldShowCurrentFlowLoading;
  const shouldShowSignInFooterSkip = showFooterSkip && !shouldShowCurrentFlowLoading;

  const handleProductSelection = useCallback((value: string) => {
    const nextProduct = value as SignInProduct;

    setPendingProductSelection(nextProduct);
    hydrateWorkspaceSelectorState();

    if (nextProduct === SIGN_IN_PRODUCT.COMPANY && currentUser?.is_staff) {
      navigate(getPreviousWorkboardRoute(currentUser.id) || WORKBOARD_CURRENT_VALUATIONS_URL);
      return;
    }

    updateRouteState({
      product: nextProduct,
      view: SIGN_IN_VIEW.WORKSPACE_SELECTION,
    });
  }, [currentUser?.id, currentUser?.is_staff, hydrateWorkspaceSelectorState, navigate, updateRouteState]);

  return (
    <AuthPageStack>
      {view !== SIGN_IN_VIEW.TWO_FACTOR ? (
        <AuthFormHeader
          logoAlt={ssoDiscovery?.firm_name ?? 'Organization'}
          firmName={ssoDiscovery?.firm_name}
          iconUrl={ssoDiscovery?.firm_icon_url}
          logoUrl={ssoDiscovery?.firm_logo_url}
          title={(
            <SignInHeaderTitle
              resolvedProduct={resolvedProduct}
              shouldHideHeaderTitle={shouldHidePostAuthLoadingHeader}
              view={view}
            />
          )}
        />
      ) : null}

      <SignInAlerts
        authErrorAlertContent={authErrorAlertContent}
        authRequirementsError={authRequirements.error}
        dismissAuthError={dismissAuthError}
        dismissRequirementsError={() => setIsRequirementsErrorDismissed(true)}
        isRequirementsErrorDismissed={isRequirementsErrorDismissed}
      />
      <SignInStepContent
        currentLoadingLabel={currentLoadingLabel}
        currentUser={currentUser}
        handleGoogleLogin={handleGoogleLogin}
        handleIdentifierSubmit={handleIdentifierSubmit}
        handleMicrosoftLogin={handleMicrosoftLogin}
        handlePasswordSubmit={handlePasswordSubmit}
        handleProductSelection={handleProductSelection}
        handleTermsSubmit={handleTermsSubmit}
        handleTwoFactorComplete={handleTwoFactorComplete}
        hasProductAccess={Boolean(productAccess)}
        identifier={identifier}
        identifierForm={identifierForm}
        isIdentifierSubmitting={isIdentifierStepSubmitting}
        isPasswordSubmitting={isPasswordSubmitting}
        isSocialPending={isSocialStepPending}
        isTermsSubmitting={isTermsSubmitting}
        logOutUser={logOutUser}
        lowerPermissions={authRequirements.lowerPermissions}
        passwordForm={passwordForm}
        pendingProductSelection={pendingProductSelection}
        productSelectionOptions={productSelectionOptions}
        rememberSignIn={rememberSignIn}
        resetPasswordHref={resetPasswordHref}
        resolvedProduct={resolvedProduct}
        returnTo={safeReturnTo}
        setRememberSignInPersisted={setRememberSignInPersisted}
        shouldShowCurrentFlowLoading={shouldShowCurrentFlowLoading}
        socialProviderPending={socialProviderPending}
        ssoDiscovery={ssoDiscovery}
        termsForm={termsForm}
        userHasTwoFactor={userHasTwoFactor}
        view={view}
      />
      <SignInFooter
        isIdentifierSubmitting={isIdentifierStepSubmitting}
        isPasswordSubmitting={isPasswordSubmitting}
        safeReturnTo={safeReturnTo}
        showFooterSkip={shouldShowSignInFooterSkip}
        showSignUpFooter={shouldShowSignInFooter}
        signUpHref={signUpHref}
      />
    </AuthPageStack>
  );
};

export default SignIn;
